import { Injectable, InjectionToken } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

import { ApiRoutes } from '../../../../common/api-routes';
import {select, Store} from '@ngrx/store';
import {ResetSearchCrewMemberUK, ResetSearchCrewMemberUSA, SearchCrewMemberUK, SearchCrewMemberUSA} from '../../actions/ui.action';
import {LiteStartcard, LiteStartcardUk} from '../../../../common/start-card.model';
import {selectCrewDetailsUK, selectCrewDetailsUSA} from '../../selectors/ui.selectors';
import {map} from 'rxjs/operators';

export interface CrewDetails {
  firstName: string;
  lastName: string;
  startcardId: string;
}
/** Define the token for the crew picker service
 * The `ICrewPickerService<LiteStartcard | LiteStartcardUk>` type is used here to provide flexibility
 * while maintaining type safety for the crew picker service. By using a union type (`LiteStartcard | LiteStartcardUk`),
 * the service can handle both `LiteStartcard` (used for the USA) and `LiteStartcardUk` (used for the UK) data models.
 */
export const CrewPickerServiceToken = new InjectionToken('ICrewPickerService');

/** Interface for Crew Picker Service with a generic type
 * The use of a generic interface (ICrewPickerService<T>) allows
 * the same functionality (searching for crew members)
 * to be reused for both the USA and UK services.
 * This reduces redundancy in code and promotes maintainability.
 */
export interface ICrewPickerService {
  crewMemberDetailsSelector: Observable<CrewDetails[]>;
  resetSearchCrewList(): void;
  dispatchSearchCrew(searchString: string, projectId: string, userType?: string): void;
}

// Service for Crew Picker USA
@Injectable({
  providedIn: 'root'
})
export class CrewPickerUSAService implements ICrewPickerService {
  crewMemberDetailsSelector: Observable<CrewDetails[]>;
  constructor(private http: HttpClient, private _store: Store<any>) {
    this.crewMemberDetailsSelector = this._store.pipe(
      select(selectCrewDetailsUSA),
      map(crewDetails => crewDetails)
    );
  }
  // The method to reset search crew members
  resetSearchCrewList(): void {
    this._store.dispatch(ResetSearchCrewMemberUSA());
  }
  // The method to search crew members
  searchCrewMember(searchString: string, projectId: string, userType?: string): Observable<LiteStartcard[]> {
    const params: { searchString: string; userType?: string } = { searchString };
    if (userType) {
      params.userType = userType;
    }
    return this.http.get<LiteStartcard[]>(`${ApiRoutes.StartcardSearch}/projects/${projectId}`, {params});
  }
  dispatchSearchCrew(searchString: string, projectId: string, userType?: string): void {
    this._store.dispatch(SearchCrewMemberUSA(searchString, projectId, userType));
  }
}

// Service for Crew Picker UK
@Injectable({
  providedIn: 'root'
})
export class CrewPickerUKService implements ICrewPickerService {
  crewMemberDetailsSelector: Observable<CrewDetails[]>;
  constructor(private http: HttpClient, private _store: Store<any>) {
    this.crewMemberDetailsSelector = this._store.pipe(
      select(selectCrewDetailsUK),
      map(crewDetails => crewDetails)
    );
  }
  // The method to search crew members
  searchCrewMember(searchString: string, projectId: string, userType?: string): Observable<LiteStartcardUk[]> {
    const params: { searchString: string; userType?: string } = { searchString };
    if (userType) {
      params.userType = userType;
    }
    return this.http.get<LiteStartcardUk[]>(`${ApiRoutes.StartcardSearch}/region/uk/projects/${projectId}`, {params});
  }

  resetSearchCrewList(): void {
    this._store.dispatch(ResetSearchCrewMemberUK());
  }

  dispatchSearchCrew(searchString: string, projectId: string, userType?: string): void {
    this._store.dispatch(SearchCrewMemberUK(searchString, projectId, userType));
  }
}
